/**
 * Meta's errors, said in Spanish and in terms of what to do about them.
 *
 * The API answers in English and in its own vocabulary: "The thread owner has archived or
 * deleted this conversation, or the thread does not exist" does not say whether retrying
 * is worth it, whether something needs changing, or whether nothing can be done. In red
 * under a log row, that text only conveys that something broke.
 *
 * The translation lives in the panel and not on the server on purpose. The log stores what
 * Meta said, verbatim, because that is what serves to search their documentation and what
 * has to be quoted when opening a support case; here it is translated as it is painted,
 * which also reaches the thousands of errors already stored in English.
 *
 * Order matters: the first rule that recognises the text wins, so the specific ones come
 * before the general ones.
 */

const TRANSLATIONS = [
  {
    match: /thread owner has archived|thread does not exist/i,
    text: "Instagram todavía no tenía abierto el chat con esta persona. Suele resolverse solo al reintentar.",
  },
  {
    match: /outside of allowed window|24[- ]hour|messaging window/i,
    text: "Pasaron más de 24 horas desde su último mensaje: Instagram ya no permite escribirle.",
  },
  {
    match: /Invalid OAuth|access token|session has expired|token is invalid/i,
    text: "La conexión con Instagram caducó. Vuelve a conectar la cuenta desde la configuración.",
  },
  {
    match: /permission|not authorized|insufficient scope/i,
    text: "A la aplicación le falta un permiso de Instagram para hacer esto. Revisa los permisos en Meta.",
  },
  {
    match: /rate limit|request limit reached|too many calls|reduce the amount of data/i,
    text: "Instagram limitó temporalmente las llamadas de la cuenta. Se reintenta más tarde.",
  },
  {
    match: /does not exist|cannot be loaded|missing permissions|object with id/i,
    text: "El comentario o la publicación ya no existen en Instagram: puede que se borraran.",
  },
  {
    match: /user is not available|user not found|recipient/i,
    text: "Instagram no encontró a esta persona: puede haber cerrado o restringido su cuenta.",
  },
  {
    match: /media (id )?is not available|unsupported (post )?request/i,
    text: "Instagram no admite esta acción sobre esa publicación.",
  },
  {
    match: /unknown error/i,
    text: "Instagram aceptó la petición pero respondió con un error sin detallar. No se reenvía para no duplicar el mensaje.",
  },
  {
    match: /timeout|timed out|network|fetch failed|socket|ECONNRESET|ETIMEDOUT/i,
    text: "No se pudo contactar con Instagram. Es un fallo de red y se reintenta solo.",
  },
  {
    match: /internal server error|please retry|temporarily unavailable|service unavailable/i,
    text: "Instagram tuvo un problema pasajero. Se reintenta solo.",
  },
];

/** The parenthesised code Meta heads almost all of its messages with. */
const META_CODE = /^\(#\d+\)\s*/;

/**
 * Translates what Meta said. A text already in Spanish — or one that is not recognised —
 * comes back untouched: inventing a meaning for it would be worse than leaving it in its
 * own language.
 */
export function translateMetaError(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const text = raw.replace(META_CODE, "");
  const known = TRANSLATIONS.find((entry) => entry.match.test(text));
  return known ? known.text : raw;
}

/**
 * Is the text still Meta's, untranslated?
 *
 * It decides whether showing the original alongside the translation is worth it: if it was
 * not recognised, the original is already all there is.
 */
export function isTranslatedMetaError(value: unknown): boolean {
  const raw = String(value ?? "").trim();
  return Boolean(raw) && translateMetaError(raw) !== raw;
}
